import { toViewableImageUrl } from './catalogProductImageStorage';
import { formatPublicImageUrlLine } from './coverImageAiPromptShared';

const MAX_MODULE_COVERS = 20;

function getCardCoverUrl(card) {
  const url = card?.backgroundImage?.url || card?.img || card?.image || null;
  return url ? toViewableImageUrl(url) : null;
}

function extractBrandColors(colors) {
  if (!colors || typeof colors !== 'object') return null;

  const entries = [
    ['Fundo do app', colors.background?.app],
    ['Texto do app', colors.text?.app],
    ['Título da Home', colors.text?.titleMain],
    ['Fundo de botões', colors.button?.backgroundApp],
    ['Texto de botões', colors.button?.textApp],
    ['Ícones do header', colors.iconsMain?.icons],
    ['Ícone de cadeado', colors.iconsMain?.iconLock],
  ].filter(([, value]) => typeof value === 'string' && value.trim());

  return entries.length ? entries : null;
}

function extractModuleCovers(categories = [], { maxItems = MAX_MODULE_COVERS } = {}) {
  const covers = [];

  categories.forEach((category) => {
    (category.cards || []).forEach((card) => {
      const url = getCardCoverUrl(card);
      if (!url) return;

      covers.push({
        moduleTitle: card.title || '(sem título)',
        categoryTitle: category.title || '(sem título)',
        url,
      });
    });
  });

  if (covers.length <= maxItems) {
    return { items: covers, omittedCount: 0 };
  }

  return {
    items: covers.slice(0, maxItems),
    omittedCount: covers.length - maxItems,
  };
}

function extractExistingProductCovers(catalogProducts = []) {
  const seen = new Set();

  return catalogProducts
    .map((product) => {
      const rawUrl = product?.catalogImage?.url;
      const url = rawUrl ? toViewableImageUrl(rawUrl) : null;
      if (!url || seen.has(url)) return null;
      seen.add(url);

      return {
        productName: product.name || '(sem nome)',
        url,
      };
    })
    .filter(Boolean);
}

export function buildAppVisualReferencesForAi({
  colors = null,
  logoAuth = null,
  imageApp = null,
  categories = [],
  catalogProducts = [],
} = {}) {
  const { items: moduleCovers, omittedCount: omittedModuleCovers } = extractModuleCovers(categories);

  return {
    colors: extractBrandColors(colors),
    logoUrl: logoAuth?.url ? toViewableImageUrl(logoAuth.url) : null,
    bannerUrls: imageApp?.url
      ? Object.values(imageApp.url)
          .filter(Boolean)
          .map((url) => toViewableImageUrl(url))
      : [],
    moduleCovers,
    omittedModuleCovers,
    existingProductCovers: extractExistingProductCovers(catalogProducts),
  };
}

function formatBrandColorsSection(colorEntries) {
  const lines = colorEntries.map(([label, value]) => `- ${label}: ${value}`);
  return `Identidade visual — cores (Edição):\n${lines.join('\n')}`;
}

function formatModuleCoversSection(moduleCovers, omittedModuleCovers = 0) {
  const lines = moduleCovers.map(
    (cover) =>
      `- Módulo: ${cover.moduleTitle} — Categoria: ${cover.categoryTitle}\n  URL pública da imagem: ${cover.url}`
  );

  if (omittedModuleCovers > 0) {
    lines.push(`(+${omittedModuleCovers} capas de módulos omitidas)`);
  }

  return `Capas de módulos do catálogo (referência de estilo) — URL pública da imagem em cada linha:\n${lines.join('\n')}`;
}

function formatExistingProductCoversSection(existingProductCovers) {
  const lines = existingProductCovers.map(
    (cover) =>
      `- Produto na vitrine: ${cover.productName}\n  URL pública da imagem: ${cover.url}`
  );

  return `Capas de produtos já cadastradas na vitrine — URL pública da imagem em cada linha:\n${lines.join('\n')}`;
}

export function formatAppVisualReferencesForPrompt(refs = {}) {
  const sections = [];

  if (refs.colors?.length) {
    sections.push(formatBrandColorsSection(refs.colors));
  }

  if (refs.logoUrl) {
    sections.push(`Logo do app:\n${formatPublicImageUrlLine(refs.logoUrl)}`);
  }

  if (refs.bannerUrls?.length) {
    const lines = refs.bannerUrls.map(
      (url, index) => `- Banner ${index + 1}: ${url}`
    );
    sections.push(
      `Banners da Home (Edição) — URL pública da imagem em cada linha:\n${lines.join('\n')}`
    );
  }

  if (refs.moduleCovers?.length) {
    sections.push(formatModuleCoversSection(refs.moduleCovers, refs.omittedModuleCovers));
  }

  if (refs.existingProductCovers?.length) {
    sections.push(formatExistingProductCoversSection(refs.existingProductCovers));
  }

  if (!sections.length) {
    return '(nenhuma referência visual encontrada no app — peça Branding Book, identidade visual e fotos/referências ao usuário)';
  }

  return sections.join('\n\n');
}

export function hasAppVisualReferences(refs = {}) {
  return Boolean(
    refs.colors?.length ||
      refs.logoUrl ||
      refs.bannerUrls?.length ||
      refs.moduleCovers?.length ||
      refs.existingProductCovers?.length
  );
}
